import { useEffect } from "react";
import { exportDocument } from "@/shared/lib/exportDocument";

const BAR_CLASS = "table-export-bar";
const COMPANY_BAR_CLASS = "company-sort-bar";
const BUTTON_CLASS =
  "rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition-colors hover:border-accent hover:bg-accent/20";

/** Même langage que le bouton Imprimer : carte, coins arrondis, icône, survol accent. */
const EXPORT_BUTTON_CLASS =
  "inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-border bg-card px-3.5 text-sm font-medium text-foreground shadow-[var(--shadow-sm)] transition-colors duration-150 hover:border-accent hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

const PDF_ICON = [
  "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z",
  "M14 2v5a1 1 0 0 0 1 1h5",
  "M10 9H8",
  "M16 13H8",
  "M16 17H8",
];

const EXCEL_ICON = [
  "M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z",
  "M14 2v5a1 1 0 0 0 1 1h5",
  "M8 13h2",
  "M14 13h2",
  "M8 17h2",
  "M14 17h2",
];

const SKIPPED_COLUMNS = new Set(["", "#", "actions", "action", "photo"]);

const text = (cell: Element) => (cell as HTMLElement).innerText.replace(/\s+/g, " ").trim();

/** Lit le tableau affiché, sans les colonnes de boutons ni les lignes de message (« Aucun résultat »). */
function readTable(table: HTMLTableElement): { headers: string[]; rows: string[][] } {
  const heads = [...table.querySelectorAll("thead th")].map(text);
  const kept = heads.map((head, index) => (SKIPPED_COLUMNS.has(head.toLowerCase()) ? -1 : index)).filter((index) => index >= 0);
  const rows = [...table.querySelectorAll("tbody tr")]
    .filter((row) => !(row as HTMLElement).hidden)
    .map((row) => [...row.children])
    .filter((cells) => cells.length === heads.length)
    .map((cells) => kept.map((index) => text(cells[index])));

  return { headers: kept.map((index) => heads[index]), rows };
}

function icon(paths: string[]): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "15");
  svg.setAttribute("height", "15");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  paths.forEach((d) => {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", d);
    svg.append(path);
  });
  return svg;
}

function button(label: string, paths: string[], onClick: () => void): HTMLButtonElement {
  const element = document.createElement("button");
  element.type = "button";
  element.className = EXPORT_BUTTON_CLASS;
  element.append(icon(paths), document.createTextNode(label));
  element.addEventListener("click", onClick);
  return element;
}

function addBar(table: HTMLTableElement): void {
  const run = (format: "pdf" | "excel") => {
    const { headers, rows } = readTable(table);
    const title = document.querySelector("main h1")?.textContent?.trim() || "Tableau";
    void exportDocument(format, title.toLowerCase().replace(/[^a-z0-9]+/g, "-"), headers, rows, title);
  };
  const bar = document.createElement("div");
  bar.className = `${BAR_CLASS} mb-3 flex justify-end gap-2 print:hidden`;
  bar.append(button("PDF", PDF_ICON, () => run("pdf")), button("Excel", EXCEL_ICON, () => run("excel")));
  (bar as HTMLElement & { exportedTable?: HTMLTableElement }).exportedTable = table;
  table.before(bar);
  table.dataset.exportBar = "1";
}

function fold(value: string): string {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Colonne « Entreprise », pas « Matricule entreprise ». */
function companyColumn(table: HTMLTableElement): number {
  return [...table.querySelectorAll("thead th")].findIndex((cell) => {
    const label = fold(text(cell));
    return label === "entreprise" && !label.includes("matricule");
  });
}

function pageFiltersCompany(table: HTMLTableElement): boolean {
  const main = table.closest("main");
  if (!main) return false;
  return [...main.querySelectorAll("select")].some((select) => {
    if (select.dataset.companySortFilter === "1") return false;
    return [...select.options].some((option) => fold(option.text) === "toutes les entreprises");
  });
}

function companyValues(table: HTMLTableElement, column: number, headCount: number): string[] {
  const values = new Set<string>();
  table.querySelectorAll("tbody tr").forEach((row) => {
    const cells = [...row.children];
    if (cells.length !== headCount) return;
    const value = text(cells[column]);
    if (value && value !== "—") values.add(value);
  });
  return [...values].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
}

function applyCompanyControls(table: HTMLTableElement, column: number): void {
  const headCount = table.querySelectorAll("thead th").length;
  const body = table.querySelector("tbody");
  if (!body) return;
  const filter = table.dataset.companyValue ?? "";
  const sort = table.dataset.companySort ?? "";
  const rows = [...body.querySelectorAll("tr")].filter((row) => row.children.length === headCount);
  rows.forEach((row) => {
    const hidden = filter !== "" && fold(text(row.children[column])) !== fold(filter);
    if (row.hidden !== hidden) row.hidden = hidden;
  });
  if (sort !== "asc" && sort !== "desc") return;
  const sorted = [...rows].sort((a, b) => {
    const result = text(a.children[column]).localeCompare(text(b.children[column]), "fr", { sensitivity: "base", numeric: true });
    return sort === "asc" ? result : -result;
  });
  if (sorted.some((row, index) => row !== rows[index])) {
    sorted.forEach((row) => body.appendChild(row));
  }
}

function companyBarFor(table: HTMLTableElement): HTMLElement | null {
  let node = table.previousElementSibling;
  while (node && node.tagName !== "TABLE") {
    if (node.classList.contains(COMPANY_BAR_CLASS)) return node as HTMLElement;
    node = node.previousElementSibling;
  }
  return null;
}

function syncCompanyBar(table: HTMLTableElement): void {
  const column = companyColumn(table);
  const existing = companyBarFor(table);
  if (column < 0 || pageFiltersCompany(table) || table.querySelector("input, select, textarea")) {
    existing?.remove();
    delete table.dataset.companyBar;
    return;
  }
  const headCount = table.querySelectorAll("thead th").length;
  const options = companyValues(table, column, headCount);
  if (options.length === 0) {
    existing?.remove();
    delete table.dataset.companyBar;
    return;
  }

  let bar = existing;
  if (!bar) {
    bar = document.createElement("div");
    bar.className = `${COMPANY_BAR_CLASS} mb-2 flex flex-wrap items-center gap-2 print:hidden`;
    const label = document.createElement("label");
    label.className = "flex items-center gap-2 text-xs font-medium text-muted-foreground";
    label.textContent = "Entreprise";
    const select = document.createElement("select");
    select.dataset.companySortFilter = "1";
    select.className = BUTTON_CLASS;
    select.setAttribute("aria-label", "Filtrer et trier par entreprise");
    select.addEventListener("change", () => {
      table.dataset.companyValue = select.value;
      applyCompanyControls(table, column);
    });
    const sort = document.createElement("button");
    sort.type = "button";
    sort.className = BUTTON_CLASS;
    sort.dataset.companySortButton = "1";
    sort.addEventListener("click", () => {
      const next = table.dataset.companySort === "asc" ? "desc" : "asc";
      table.dataset.companySort = next;
      sort.textContent = next === "asc" ? "Tri A → Z" : "Tri Z → A";
      applyCompanyControls(table, column);
    });
    sort.textContent = "Trier par entreprise";
    label.append(select);
    bar.append(label, sort);
    table.before(bar);
    table.dataset.companyBar = "1";
  }

  const select = bar.querySelector("select");
  if (!select) return;
  const current = table.dataset.companyValue ?? "";
  const wanted = ["", ...options];
  const same =
    select.options.length === wanted.length && wanted.every((value, index) => select.options[index]?.value === value);
  if (!same) {
    select.replaceChildren(
      ...wanted.map((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value === "" ? "Toutes les entreprises" : value;
        return option;
      }),
    );
    select.value = wanted.includes(current) ? current : "";
    table.dataset.companyValue = select.value;
  }
  applyCompanyControls(table, column);
}

/** Saisie (cellules de formulaire), tableau vide ou carte qui exporte déjà toutes ses lignes : pas de boutons. */
function isExportable(table: HTMLTableElement): boolean {
  if (table.dataset.exportBar || table.querySelector("input, select, textarea")) return false;
  if (table.closest(".theme-card")?.querySelector("[data-table-export]")) return false;
  return readTable(table).rows.length > 0;
}

/**
 * Ajoute les boutons PDF et Excel aux tableaux qui n'ont pas de menu d'export propre (fiches, tableaux de bord…),
 * y compris les futurs tableaux. Les listes paginées passent par ExportMenu, qui exporte toutes les lignes filtrées.
 */
export function TableExportEnhancer() {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;

    const refresh = () => {
      main.querySelectorAll<HTMLElement>(`.${BAR_CLASS}`).forEach((bar) => {
        const table = (bar as HTMLElement & { exportedTable?: HTMLTableElement }).exportedTable;
        if (!table?.isConnected) bar.remove();
      });
      main.querySelectorAll("table").forEach((table) => {
        syncCompanyBar(table);
        if (isExportable(table)) addBar(table);
      });
    };

    let timer = 0;
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(refresh, 200);
    });
    observer.observe(main, { childList: true, subtree: true });
    refresh();

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);

  return null;
}
